// Attachments on a question: images, PDFs and text files.
//
// The browser uploads each file first (POST /api/uploads → R2), then sends the
// question with one content part per file that points at the upload:
//
//   { type: 'image',    source: { type: 'url', value: '/api/uploads/up_…', mimeType }, metadata: { uploadId, filename, size } }
//   { type: 'document', source: { type: 'url', value: '/api/uploads/up_…', mimeType }, metadata: { uploadId, filename, size } }
//
// The transcript keeps only these small references; the Worker swaps in the
// file's bytes when it calls the model (worker/ai/attachments.js).

export const MAX_ATTACHMENTS = 5

const MB = 1024 * 1024

/** What can be attached, and how big it may be. */
export const ATTACHMENT_KINDS = {
  image: {
    label: 'image',
    plural: 'Images',
    extensions: ['png', 'jpg', 'jpeg', 'gif', 'webp'],
    types: ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
    maxBytes: 5 * MB,
  },
  pdf: {
    label: 'PDF',
    plural: 'PDFs',
    extensions: ['pdf'],
    types: ['application/pdf'],
    maxBytes: 10 * MB,
  },
  text: {
    label: 'text file',
    plural: 'Text files',
    extensions: ['txt', 'csv', 'tsv', 'md', 'json', 'xml', 'yaml', 'yml', 'log', 'sql'],
    types: [
      'text/plain',
      'text/csv',
      'text/tab-separated-values',
      'text/markdown',
      'application/json',
      'application/xml',
      'text/xml',
      'application/x-yaml',
      'text/yaml',
      'application/sql',
    ],
    maxBytes: 1 * MB,
  },
}

/** For <input accept>: the image button and the file button. */
export const IMAGE_ACCEPT = ATTACHMENT_KINDS.image.extensions.map((e) => `.${e}`).join(',')
export const FILE_ACCEPT = [...ATTACHMENT_KINDS.pdf.extensions, ...ATTACHMENT_KINDS.text.extensions].map((e) => `.${e}`).join(',')

const extensionOf = (name) => String(name ?? '').split('.').pop()?.toLowerCase() ?? ''

/** 'image' | 'pdf' | 'text' | null, from the file name first, then its type. */
export function kindOf(name, mime) {
  const ext = extensionOf(name)
  for (const [kind, spec] of Object.entries(ATTACHMENT_KINDS)) if (spec.extensions.includes(ext)) return kind
  const type = String(mime ?? '').toLowerCase().split(';')[0].trim()
  for (const [kind, spec] of Object.entries(ATTACHMENT_KINDS)) if (spec.types.includes(type)) return kind
  return null
}

/** The type to store and send: the browser's when it matches the kind, else the kind's default. */
export function mimeFor(kind, name, mime) {
  const type = String(mime ?? '').toLowerCase().split(';')[0].trim()
  const spec = ATTACHMENT_KINDS[kind]
  if (spec?.types.includes(type)) return type
  if (kind === 'image') return { jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp' }[extensionOf(name)] ?? 'image/png'
  if (kind === 'pdf') return 'application/pdf'
  return { csv: 'text/csv', tsv: 'text/tab-separated-values', md: 'text/markdown', json: 'application/json', xml: 'application/xml' }[extensionOf(name)] ?? 'text/plain'
}

/** Why a file can't be attached, in words the user can act on; null when it can. */
export function attachmentProblem(name, mime, size) {
  const kind = kindOf(name, mime)
  if (!kind) return `${name} can't be attached. Attach images (PNG, JPG, GIF, WebP), PDFs or text files (TXT, CSV, JSON, XML, MD).`
  const { maxBytes, plural } = ATTACHMENT_KINDS[kind]
  if (size > maxBytes) return `${name} is ${formatBytes(size)}. ${plural} can be up to ${formatBytes(maxBytes)}.`
  if (size === 0) return `${name} is empty.`
  return null
}

export function formatBytes(bytes) {
  const n = Number(bytes) || 0
  if (n < 1024) return `${n} B`
  if (n < MB) return `${Math.round(n / 1024)} KB`
  const mb = n / MB
  return `${Number.isInteger(mb) ? mb : mb.toFixed(mb < 10 ? 1 : 0)} MB`
}

export const uploadUrl = (id) => `/api/uploads/${id}`
const UPLOAD_URL = /^\/api\/uploads\/(up_[a-f0-9]{32})$/

export function uploadIdFromUrl(url) {
  return UPLOAD_URL.exec(String(url ?? ''))?.[1] ?? null
}

/** The content part that carries one uploaded file in a question. */
export function attachmentPart(upload) {
  return {
    type: upload.kind === 'image' ? 'image' : 'document',
    source: { type: 'url', value: uploadUrl(upload.id), mimeType: upload.mime },
    metadata: { uploadId: upload.id, filename: upload.name, size: upload.size },
  }
}

/**
 * What to pass to sendMessage: plain text when there are no files, otherwise
 * the text part followed by one part per file.
 */
export function questionContent(text, uploads = []) {
  if (!uploads.length) return text
  return { content: [...(text ? [{ type: 'text', content: text }] : []), ...uploads.map(attachmentPart)] }
}

/** Parts of a UI message (parts) or model message (content) that are attachments. */
function contentParts(message) {
  if (Array.isArray(message?.parts)) return message.parts
  if (Array.isArray(message?.content)) return message.content
  return []
}

/** The files attached to a message: [{ id, kind, name, mime, size, url }]. */
export function attachmentsOf(message) {
  return contentParts(message)
    .filter((part) => (part?.type === 'image' || part?.type === 'document') && part.source?.type === 'url')
    .map((part) => {
      const id = uploadIdFromUrl(part.source.value) ?? part.metadata?.uploadId ?? null
      const name = part.metadata?.filename ?? (part.type === 'image' ? 'Image' : 'File')
      const mime = part.source.mimeType ?? ''
      return {
        id,
        kind: part.type === 'image' ? 'image' : kindOf(name, mime) ?? 'text',
        name,
        mime,
        size: part.metadata?.size ?? null,
        url: part.source.value,
      }
    })
}

/** True when a part is media the server cannot accept (inline data, outside links). */
export function isForeignMedia(part) {
  if (part?.type === 'text') return false
  return !(part?.source?.type === 'url' && uploadIdFromUrl(part.source.value))
}
